import React, { useMemo, useContext } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import useT from '../../../i18ns/admin/useT'
import { InitialFormFunc } from '../../../utils/default/ComplexFlowHook'
import { FormContext } from './WithdrawalRCSlipDetailDialog'
import { RCStatusType } from '@golden/gdk-admin'
import {
  defaultEditorState,
  EditorState
} from '@golden/tiptap-react'
import RedTextBubbleEditor from '../../default/form/textEditor/RedTextBubbleEditor'

export interface WithdrawRCSlipFormType {
  status: RCStatusType
  memo: EditorState
}

export const initialForm: InitialFormFunc<WithdrawRCSlipFormType> = (defaultForm) => ({
  status: RCStatusType.IN_PROGRESS,
  memo: defaultEditorState,
  ...defaultForm
})

const useStyles = makeStyles((theme) => ({
  buttons: {
    marginTop: -20
  },
  button: {
    borderRadius: '50px',
    width: 100
  },
  successful: {
    color: '#72d476',
    border: '1px solid #72d476',
    '&:hover, &.active': {
      border: '1px solid transparent',
      background: '#72d476',
      color: theme.palette.common.white
    }
  },
  irregular: {
    color: theme.palette.secondary.main,
    border: `1px solid ${theme.palette.secondary.main}`,
    '&:hover, &.active': {
      border: '1px solid transparent',
      background: theme.palette.secondary.main,
      color: theme.palette.common.white
    }
  },
  canceled: {
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
    '&:hover, &.active': {
      border: '1px solid transparent',
      background: theme.palette.error.main,
      color: theme.palette.common.white
    }
  },
  editor: {
    marginTop: theme.spacing(2)
  },
  editorPaper: {
    overflow: 'visible',
    position: 'relative',
    border: '1px solid rgba(0, 0, 0, 0.26)'
  },
  editorDisabled: {
    border: 'none',
    background: '#e0e0e0'
  },
  editorEnabled: {
    '&:hover': {
      border: '1px solid #808080 !important'
    }
  },
  editorError: {
    border: `1px solid ${theme.palette.error.main} !important`,
    '&:hover': {
      border: `1px solid ${theme.palette.error.main} !important`
    }
  },
  editorContent: {
    minHeight: '117px',
    overflow: 'visible',
    '& .ProseMirror': {
      minHeight: '80px',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }
  },
  helperText: {
    ...theme.typography.caption,
    lineHeight: '1rem',
    marginTop: theme.spacing(1)
  },
  helperTextError: {
    color: theme.palette.error.main
  },
  editorHeight: {
    minHeight: '117px'
  }
}))

interface RCMemoEditorProps {
  value: EditorState
  disabled: boolean
  error: boolean
  helperText: string
  placeholder: string
  onChange: (value: EditorState) => void
}

const RCMemoEditor: React.FC<RCMemoEditorProps> = React.memo((props) => {
  const {
    value,
    disabled,
    error,
    helperText,
    placeholder,
    onChange
  } = props
  const classes = useStyles()

  return (
    <>
      <Paper
        variant="outlined"
        className={clsx(
          classes.editorPaper,
          classes.editorHeight,
          disabled && classes.editorDisabled,
          !disabled && classes.editorEnabled,
          !disabled && error && classes.editorError
        )}
      >
        <RedTextBubbleEditor
          value={value}
          disabled={disabled}
          onChange={onChange}
          placeholder={placeholder}
          editorContentClassName={classes.editorContent}
        />
      </Paper>
      {helperText && (
        <div className={clsx(classes.helperText, error && classes.helperTextError)}>
          {helperText}
        </div>
      )}
    </>
  )
})

const MemoInput: React.FC<{ disabled: boolean }> = React.memo((props) => {
  const { disabled } = props
  const { value, handleChange, error } = useContext(FormContext)
  const { t } = useT()
  const classes = useStyles()
  const helperText = useMemo(() => {
    return [RCStatusType.IRREGULAR, RCStatusType.CANCELED].includes(value.status) && !disabled ? t('placeholder.editBeforeSubmit', { item: t('common.memo') }) : ' '
  }, [t, value.status, disabled])

  return (
    <Box className={classes.editor}>
      <RCMemoEditor
        value={value.memo}
        onChange={handleChange('memo')}
        disabled={disabled}
        error={error.memo !== null}
        helperText={helperText}
        placeholder={`RC ${t('common.memo')}`}
      />
    </Box>
  )
})

const StatusButtons: React.FC<{ hideIrregularButton?: boolean }> = React.memo((props) => {
  const { hideIrregularButton } = props
  const { t } = useT()
  const classes = useStyles()
  const { value, handleChange } = useContext(FormContext)
  const buttons = useMemo(() => {
    return [
      { name: t('rcStatus.successful'), value: RCStatusType.SUCCESSFUL },
      ...hideIrregularButton ? [] : [{ name: t('rcStatus.irregular'), value: RCStatusType.IRREGULAR }],
      { name: t('rcStatus.cancel'), value: RCStatusType.CANCELED }
    ]
  }, [t, hideIrregularButton])

  return (
    <Grid container spacing={1} className={classes.buttons}>
      {
        buttons.map(item =>
          (
            <Grid item key={item.value}>
              <Button
                className={clsx(classes.button, classes[item.value as 'successful' | 'irregular' | 'canceled'], value.status === item.value ? 'active' : null)}
                onClick={() => { handleChange('status')(item.value) }}
              >
                {item.name}
              </Button>
            </Grid>
          )
        )
      }
    </Grid>
  )
})

const EditWithdrawalRCSlipForm: React.FC<{ writable: boolean, hideIrregularButton?: boolean }> = (props) => {
  const { writable, hideIrregularButton } = props
  return (
    <Box display='flex' alignItems='center'>
      <Box flex={1} minWidth={0}>
        <MemoInput disabled={!writable} />
      </Box>
      {
        writable && (
          <Box marginLeft={1} flexShrink={0}>
            <StatusButtons hideIrregularButton={hideIrregularButton} />
          </Box>
        )
      }
    </Box>
  )
}

export default EditWithdrawalRCSlipForm
